import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Text,
    Button,
    useMultiStyleConfig,
    useBreakpointValue,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouterLink} from 'react-router-dom'
import {Link as ChakraLink} from '@chakra-ui/react'
import {getGlobalLink} from '../../utils/contentstack-utils'
import useDamImage from '../../custom-hooks/use-dam-image'
import {remCalc} from '../../theme/_utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const PopoverContentImage = ({
    first_level_image_section = {},
    selected_second_level_category = {},
    categoryData = {},
    isDrawer = false,
}) => {
    const styles = useMultiStyleConfig('BraunMenu', {})
    const popoverContent = styles?.popoverContent || {}
    const isMobile = useBreakpointValue({base: true, md: false})
    const {locale} = useMultiSite()

    const selectedCategory = Object.keys(selected_second_level_category || {}).length
        ? structuredClone(selected_second_level_category)
        : structuredClone(first_level_image_section)

    function hasAnyValue(obj) {
        if (obj == null) return false
        if (Array.isArray(obj)) {
            return obj.some((item) => hasAnyValue(item))
        }
        if (typeof obj === 'object') {
            return Object.values(obj).some((value) => hasAnyValue(value))
        }
        return Boolean(obj)
    }

    let text = {}
    let image = {}
    let cta = {}

    const {label: secondLevelFallbackLabel} = useMemo(() => {
        if (!categoryData) return {}
        return (
            getGlobalLink({
                link: selectedCategory?.second_level_category,
                categoriesData: categoryData,
                locale: locale?.alias,
            }) || {}
        )
    }, [selected_second_level_category])

    let isSecondLevel = false
    //eslint-disable-next-line
    if (selectedCategory.hasOwnProperty('first_level_category_image')) {
        isSecondLevel = false
        image = selectedCategory?.first_level_category_image
        const isWhite = selectedCategory?.image_elements_color === 'White'
        text = {
            color: isWhite ? 'text.inverse' : 'text.default',
            title: selectedCategory?.title_image,
            subtitle: selectedCategory?.subtitle,
        }
        cta = {
            ...(selectedCategory?.cta || {}),
            button_color: isWhite ? 'background.dark' : 'background.default',
        }
    } else {
        isSecondLevel = true
        image = selectedCategory?.second_level_image_section?.second_level_category_image
        const isWhite =
            selectedCategory?.second_level_image_section?.image_elements_color === 'White'
        text = {
            color: isWhite ? 'text.inverse' : 'text.default',
            title:
                selectedCategory?.second_level_image_section?.title_override?.trim() !== ''
                    ? selectedCategory?.second_level_image_section?.title_override
                    : null,
            subtitle: selectedCategory?.second_level_image_section?.subtitle,
        }

        const override = structuredClone(
            selectedCategory?.second_level_image_section?.cta_override || {},
        )
        const fallback = structuredClone(selectedCategory?.second_level_category || {})
        const useOverride = hasAnyValue(override)

        cta = {
            ...(useOverride ? override : fallback),
            button_color: isWhite ? 'background.dark' : 'background.default',
            show_cta: selectedCategory?.second_level_image_section?.show_cta_on_image,
        }
    }

    const defaultImage = image
    const fallbackImage = first_level_image_section?.first_level_category_image

    const primaryDam = useDamImage(defaultImage)
    const fallbackDam = useDamImage(fallbackImage)

    let damImage = primaryDam
    if (isDrawer) {
        damImage = primaryDam?.url !== '' ? primaryDam : fallbackDam
    }
    const {url, label, isAbsolute} = useMemo(() => {
        if (!categoryData) return {}
        return (
            getGlobalLink({
                link: cta,
                categoriesData: categoryData,
                locale: locale?.alias,
            }) || {}
        )
    }, [cta, categoryData, locale])

    const validUrl = url && url.trim().length > 0
    const renderButton = label && ((isSecondLevel && cta?.show_cta) || !isSecondLevel) && (
        <Button
            variant="primary"
            as={validUrl ? (isAbsolute ? ChakraLink : RouterLink) : 'button'}
            href={isAbsolute ? url : ''}
            to={!isAbsolute ? url : ''}
            target={isAbsolute ? '_blank' : undefined}
            rel={isAbsolute ? 'noopener noreferrer' : undefined}
        >
            {label}
        </Button>
    )

    const imageStyleProps = {
        ...popoverContent?.imageContainer,
        bgImage: `url(${damImage?.url})`,
    }

    if ((!text.title || text.title === '') && secondLevelFallbackLabel && isSecondLevel) {
        text.title = secondLevelFallbackLabel
    }

    if (!isDrawer) {
        return (
            <Flex {...imageStyleProps}>
                <Flex {...popoverContent?.imageTitleContainer}>
                    <Text {...popoverContent?.textTitle} color={text.color}>
                        {text?.title}
                    </Text>
                    <Text {...popoverContent?.textSubtitle} color={text.color}>
                        {text?.subtitle}
                    </Text>
                </Flex>
                <Flex>{renderButton}</Flex>
            </Flex>
        )
    }

    if (isDrawer && !isMobile) {
        return (
            <Flex {...imageStyleProps} marginBottom={remCalc(100)}>
                <Flex {...popoverContent?.imageTitleContainer}>
                    <Text {...popoverContent?.textTitle} color={text.color}>
                        {text?.title}
                    </Text>
                    <Text {...popoverContent?.textSubtitle} color={text.color}>
                        {text?.subtitle}
                    </Text>
                </Flex>
                <Flex>{renderButton}</Flex>
            </Flex>
        )
    }

    return (
        <Flex {...popoverContent?.drawerVariantContainer}>
            <Flex {...imageStyleProps} />
            <Flex {...popoverContent?.drawerContentSubContainer} marginBottom={remCalc(100)}>
                <Flex {...popoverContent?.drawerContentSubContainer}>
                    <Text {...popoverContent?.textTitle}>{text?.title}</Text>
                    <Text {...popoverContent?.textSubtitle}>{text?.subtitle}</Text>
                </Flex>
                <Flex>{renderButton}</Flex>
            </Flex>
        </Flex>
    )
}

PopoverContentImage.propTypes = {
    first_level_image_section: PropTypes.object,
    second_level_image_section: PropTypes.object,
    selected_second_level_category: PropTypes.object,
    categoryData: PropTypes.object,
    isDrawer: PropTypes.bool,
}

export default PopoverContentImage
